import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "src/store";

import { showDetails, updateSearchQuery } from "@dashboard/devices/store/index";
import { selectDevicesByGroupIds } from "@dashboard/devices/store/selectors/devices";
import { EventReport } from "@dashboard/devices/types";
import { selectGroups, selectGroupsByIds } from "@dashboard/groups/store/selectors";
import { selectLicenseById } from "@dashboard/licenses/store/selectors";
import { renderEventContent } from "@dashboard/shared/components/events";
import { Collapse, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useFetchBuildsForProductsQuery } from "@products/api";
import { useCurrentWorkspaceProducts } from "@products/api/helpers";
import ElasticService from "@services/elasticService";
import TimeService from "@services/timeService/timeService";
import UserSettingsService from "@services/userSettingsService";
import { Tooltip } from "@shared/Tooltip";

import { useStyles } from "./EventOverviewModal.styles";

export const EventItem = ({ e, groupIds }: { e: EventReport; groupIds: number[] }) => {
    const { classes } = useStyles();
    const [isExpanded, setIsExpanded] = React.useState(false);
    const { t } = useTranslation(["modals"]);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();

    const devices = useSelector((state) => selectDevicesByGroupIds(state, groupIds));
    const groups = useSelector((state) => selectGroupsByIds(state, groupIds));
    const allGroups = useSelector(selectGroups);

    const { data: products = [] } = useCurrentWorkspaceProducts();
    const { data: builds = [] } = useFetchBuildsForProductsQuery(products.map((product) => product.id));

    const isLicenseEvent = e.eventTypeName.includes("LicenseEvent");
    const isDeviceEvent = e.eventTypeName.includes("DeviceEvent");
    const isGroupEvent = e.eventTypeName.includes("GroupEvent");

    const license = useSelector((state) => (isLicenseEvent ? selectLicenseById(state, parseInt(e.entityId)) : undefined));

    const getDeviceForLicense = () => {
        if (license?.device) {
            return license.device;
        }
        return devices.find((d) => d?.licenseId === license?.id);
    };

    const deviceForLicense = isLicenseEvent ? getDeviceForLicense() : undefined;
    const deviceForDeviceEvent = isDeviceEvent ? devices.find((d) => d?.id.toString() === e.entityId) : undefined;

    const device = deviceForLicense || deviceForDeviceEvent;
    const deviceLicenseId = device?.licenseId;

    const getGroup = () => {
        const groupById = groups.find((g) => g.id === e.groupId);
        if (groupById) {
            return groupById;
        }

        if (isGroupEvent) {
            const groupByEntityId = groups.find((g) => g.id.toString() === e.entityId);
            if (groupByEntityId) {
                return groupByEntityId;
            }
        }

        if (isLicenseEvent && license) {
            const groupByLicense = groups.find((g) => g.licenseIds.includes(license.id));
            if (groupByLicense) {
                return groupByLicense;
            }
        }

        if (isDeviceEvent && deviceLicenseId) {
            const groupByDeviceLicense = groups.find((g) => g.licenseIds.some((licenseId) => licenseId === deviceLicenseId));
            if (groupByDeviceLicense) {
                return groupByDeviceLicense;
            }
        }

        return undefined;
    };

    const group = getGroup();

    const handleGroupNameClick = (event: EventReport) => {
        UserSettingsService.setWorkspaceValue("groupPage.columnFilters", []);
        dispatch(
            showDetails({
                type: "group",
                selectedId: event.groupId,
            }),
        );
        void navigate("/dashboard/groups");

        const groupName = isLicenseEvent ? (license?.groupName ?? group?.name ?? "") : (group?.name ?? "");
        ElasticService.createTransaction("overviewPageLinkClick", {
            tab: "activity-feed-group",
            text: groupName,
        });
    };

    const handleDeviceNameClick = (event: EventReport) => {
        if (isDeviceEvent) {
            const clickedDevice = devices.find((item) => item?.id.toString() === event.entityId);
            const clickedGroup = groups.find((item) =>
                item.licenseIds.some((licenseId) => licenseId === clickedDevice?.licenseId),
            );

            if (clickedDevice?.licenseId && clickedDevice.name && clickedGroup) {
                UserSettingsService.setWorkspaceValue("devicePage.columnFilters", []);
                dispatch(updateSearchQuery(clickedDevice.serial || ""));
                dispatch(showDetails({ type: "license", selectedId: clickedDevice.licenseId }));
                void navigate("/dashboard/devices");

                ElasticService.createTransaction("overviewPageLinkClick", {
                    tab: "activity-feed-device",
                    text: clickedDevice.name,
                });
            }
        } else if (isLicenseEvent && device && group) {
            UserSettingsService.setWorkspaceValue("devicePage.columnFilters", []);
            dispatch(updateSearchQuery(device.serial || ""));
            dispatch(showDetails({ type: "license", selectedId: device.licenseId }));
            void navigate("/dashboard/devices");

            ElasticService.createTransaction("overviewPageLinkClick", {
                tab: "activity-feed-device",
                text: device.name ?? "",
            });
        }
    };

    const handleLicenseClick = isLicenseEvent
        ? () => {
              UserSettingsService.setWorkspaceValue("devicePage.columnFilters", []);
              dispatch(
                  showDetails({
                      type: "license",
                      selectedId: license?.id ?? -1,
                  }),
              );
              void navigate("/dashboard/devices");
          }
        : undefined;

    const { color, header, component, details } = renderEventContent(
        e,
        {
            license,
            device,
            group,
            allGroups,
            products,
            builds,
            navigate,
            handleDeviceNameClick,
            handleGroupNameClick,
            handleLicenseClick,
        },
        theme,
    );

    const detailsToggleLabel = isExpanded
        ? t("modals:eventModals.tooltip.hideThisEventDetails")
        : t("modals:eventModals.tooltip.showThisEventDetails");

    return (
        <div>
            <div className={classes.eventItem}>
                <div className={classes.deviceSummaryIndicator} style={{ backgroundColor: color }} />
                <div className={classes.eventContent}>
                    <div className={classes.primaryBlock}>
                        <Typography variant="body1" className={classes.primaryText}>
                            {header}
                        </Typography>
                        <div className={classes.secondaryLine}>
                            {details ? (
                                <Tooltip title={detailsToggleLabel} disableChildrenWrapper>
                                    <IconButton
                                        className={classes.detailsToggle}
                                        size="small"
                                        aria-label={detailsToggleLabel}
                                        aria-expanded={isExpanded}
                                        onClick={() => setIsExpanded((expanded) => !expanded)}
                                    >
                                        <i
                                            className={isExpanded ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-right"}
                                            style={{ fontSize: 16 }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            ) : null}
                            <Typography variant="body1" component="div" className={classes.secondaryText}>
                                {component}
                            </Typography>
                        </div>
                    </div>
                    <div className={classes.secondaryBlock}>
                        <div className={classes.secondaryBlockContent}>
                            <Typography variant="body1" className={classes.dateTimeText}>
                                {TimeService.getLocalDate(e.created, "ISO", "time")}
                            </Typography>
                        </div>
                    </div>
                </div>
            </div>
            {details ? (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <div className={classes.eventDetails}>{details}</div>
                </Collapse>
            ) : null}
        </div>
    );
};
